import * as React from 'react';
import { Typography, Box, Dialog, Container, TextField, Button, Avatar, Grid, Paper, Divider, Alert, Chip, LinearProgress } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { connect } from "react-redux";
import { useNavigate, useParams } from 'react-router-dom';
import { getOrders, packOrderItem, finalizeOrder, cancelPackingOrder } from '../../app/store/actions/imsEmployeesActions';
import moment from 'moment';
import { QrCode, Close, Store, Timer } from '@mui/icons-material';
import BarcodeScanner from "react-qr-barcode-scanner";
import { isDesktop } from 'react-device-detect';
import { notification } from 'antd';
import { isNumber } from 'lodash';

const formatLocation = (location) => {
    if (!location) return null;
    if (typeof location === 'string') return location;
    return location.name || location.code || location.label || null;
};

const getLineProgress = (item) => {
    const quantityOrdered = item.quantityOrdered ?? 0;
    const piecesRemaining = item.quantity ?? 0;
    const piecesPacked = quantityOrdered - piecesRemaining;
    const isFullyPacked = item.status === "Packed" || piecesRemaining === 0;
    const isPartiallyPacked = !isFullyPacked && piecesPacked > 0;
    const needsScan = piecesRemaining > 0;
    return { quantityOrdered, piecesRemaining, piecesPacked, isFullyPacked, isPartiallyPacked, needsScan };
};

const sortOrderItems = (items) => {
    if (!items?.length) return [];
    return [...items].sort((a, b) => {
        const aPacked = getLineProgress(a).isFullyPacked;
        const bPacked = getLineProgress(b).isFullyPacked;
        if (aPacked === bPacked) return 0;
        return aPacked ? 1 : -1;
    });
};

const getLineTitle = (item) => {
    const name = item.stockItem?.product?.name ?? 'Product';
    const locationStr = formatLocation(item.stockItem?.location);
    return locationStr ? `${name} · ${locationStr}` : name;
};

const countBoxSx = {
    width: '100%',
    py: 2,
    px: 2,
    borderRadius: 2,
    bgcolor: 'grey.50',
    border: '1px solid',
    borderColor: 'divider',
    textAlign: 'center',
};

const PiecesRemainingBox = ({ count, isFullyPacked, isPartiallyPacked, quantityOrdered, piecesPacked }) => {
    if (isFullyPacked) {
        return (
            <Box sx={countBoxSx}>
                <Typography variant="h5" fontWeight="bold" color="text.secondary">
                    {piecesPacked}/{quantityOrdered} packed ✓
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={countBoxSx}>
            <Typography variant="h2" fontWeight="bold" lineHeight={1}>
                {count}
            </Typography>
            <Typography variant="subtitle1" fontWeight="bold" letterSpacing={2} color="text.secondary">
                {count === 1 ? 'PC REMAINING' : 'PCS REMAINING'}
            </Typography>
            {isPartiallyPacked && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {piecesPacked} of {quantityOrdered} already packed
                </Typography>
            )}
        </Box>
    );
};

export const PackOrder = ({ user, getOrders, packOrderItem, cancelPackingOrder, order, orderItems }) => {
    const navigate = useNavigate()
    const orderId = useParams().orderId
    const [scanning, setScanning] = useState(false)
    const [elapsedTime, setElapsedTime] = useState(0);
    const [scanText, setScanText] = useState("");
    const [feedback, setFeedback] = useState({ type: null, message: "" });
    const successAudioRef = useRef(null);
    const errorAudioRef = useRef(null);

    useEffect(() => {
        const successAudio = new Audio('/correct.mp3');
        successAudio.preload = "auto";
        const errorAudio = new Audio('/error.mp3');
        errorAudio.preload = "auto";
        successAudioRef.current = successAudio;
        errorAudioRef.current = errorAudio;

        return () => {
            if (successAudioRef.current) {
                successAudioRef.current.pause();
                successAudioRef.current = null;
            }
            if (errorAudioRef.current) {
                errorAudioRef.current.pause();
                errorAudioRef.current = null;
            }
        };
    }, []);

    const clearFeedback = () => setFeedback({ type: null, message: "" });

    const triggerFeedback = (type, message) => {
        setFeedback({ type, message });

        const audioRef = type === 'success' ? successAudioRef : errorAudioRef;
        if (audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(() => { });
        }
    };

    const submitScan = async (payload) => {
        try {
            const response = await packOrderItem(payload, navigate);
            if (response?.status === 200 || response?.status === 201) {
                triggerFeedback('success', response?.data?.message || "Item packed successfully");
            }
        } catch (err) {
            const status = err?.response?.status;
            const errorMessage = err?.response?.data?.message;
            if (status === 404) {
                triggerFeedback('error', errorMessage || "Incorrect item scanned");
            } else {
                triggerFeedback('error', errorMessage || "Failed to process scan");
            }
        } finally {
            setScanText("");
        }
    };

    const handleScan = async (data) => {
        if (data) {
            try {
                setScanText(data)
                let json;
                try {
                    json = JSON.parse(data);
                    if (isNumber(json)) {
                        throw new Error()
                    }
                } catch (parseError) {
                    console.error("JSON parse error:", parseError);
                    console.log("Using Map ID:", data);
                    await submitScan({ orderId, productBarcode: data });
                    return;
                }

                if (!json.productId) {
                    console.error("Product ID is missing or null in scan data");
                    notification.error({
                        message: "Invalid Scan",
                        description: "Could not find product ID in scan data"
                    });
                    return;
                }

                console.log("Using product ID:", json.productId);
                await submitScan({ orderId, productId: json.productId });
            } catch (err) {
                console.error("Scan processing error:", err);
                notification.error({
                    message: "Scan Error",
                    description: "Failed to process scan data"
                });
            }
        }
    }

    useEffect(() => {
        const intervalId = setInterval(() => {
            const currentTime = order?.preparedAt ? moment(order?.preparedAt) : moment();
            const assignedTime = moment(order?.assignedAt);
            const elapsedSeconds = currentTime.diff(assignedTime, 'seconds');
            if (elapsedSeconds > 0) {
                setElapsedTime(elapsedSeconds);
            }
        }, 1000);

        return () => clearInterval(intervalId);
    }, [order?.assignedAt, order?.preparedAt]);

    useEffect(() => {
        getOrders({ orderId })
    }, [orderId])

    useEffect(() => {
        if (order && order.status === "Prepared") {
            navigate(`/ims/employee`)
        }
    }, [order, navigate])

    const sortedItems = sortOrderItems(orderItems);
    const piecesRemaining = orderItems?.reduce((sum, item) => sum + (item.quantity ?? 0), 0) ?? 0;
    const totalOrdered = orderItems?.reduce((sum, item) => sum + (item.quantityOrdered ?? 0), 0) ?? 0;
    const totalPacked = orderItems?.reduce(
        (sum, item) => sum + ((item.quantityOrdered ?? 0) - (item.quantity ?? 0)),
        0
    ) ?? 0;
    const packProgress = totalOrdered > 0 ? (totalPacked / totalOrdered) * 100 : 0;

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Paper elevation={0} sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Store sx={{ color: 'text.secondary', fontSize: 40 }} />
                                <Box>
                                    <Typography variant="h6" fontWeight="bold">
                                        {order?.vendor?.brandName}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {order?.blinkTrackingId || order?.bostaTrackingId || order?.courierTrackingId || order?.fincartCode || order?.shipbluTrackingId || order?.externalId}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center' }}>
                                <Timer sx={{ color: 'text.secondary' }} />
                                <Typography variant="h4" fontWeight="bold">
                                    {moment(elapsedTime * 1000).format("mm:ss")}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'stretch', md: 'flex-end' }, gap: 1 }}>
                                <Box
                                    sx={{
                                        ...countBoxSx,
                                        minWidth: { md: 200 },
                                        textAlign: { xs: 'center', md: 'center' },
                                    }}
                                >
                                    <Typography variant="h3" fontWeight="bold" lineHeight={1}>
                                        {piecesRemaining}
                                    </Typography>
                                    <Typography
                                        variant="subtitle2"
                                        fontWeight="bold"
                                        letterSpacing={1.5}
                                        color="text.secondary"
                                    >
                                        {piecesRemaining === 1 ? 'PC LEFT TO SCAN' : 'PCS LEFT TO SCAN'}
                                    </Typography>
                                </Box>
                                {totalOrdered > 0 && (
                                    <Box sx={{ width: '100%', maxWidth: 220 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            {totalPacked} / {totalOrdered} packed
                                        </Typography>
                                        <LinearProgress variant="determinate" value={packProgress} sx={{ mt: 0.5, borderRadius: 1 }} />
                                    </Box>
                                )}
                                <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={() => cancelPackingOrder(navigate)}
                                    startIcon={<Close />}
                                    sx={{ mt: 0.5 }}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>

                <Paper elevation={0} sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <QrCode sx={{ color: 'text.secondary', fontSize: 40 }} />
                        <Box>
                            <Typography variant="h6" fontWeight="bold">
                                Scan Products
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Scan the QR code on each product to pack it
                            </Typography>
                        </Box>
                    </Box>

                    {isDesktop ? (
                        <TextField
                            fullWidth
                            autoFocus
                            value={scanText}
                            label="Product QR Code"
                            onChange={({ target }) => setScanText(target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Tab' || e.key === 'Enter') {
                                    e.preventDefault();
                                    handleScan(scanText);
                                }
                            }}
                            InputProps={{
                                startAdornment: <QrCode sx={{ mr: 1, color: 'action.active' }} />
                            }}
                        />
                    ) : (
                        <Button
                            variant="contained"
                            fullWidth
                            size="large"
                            onClick={() => setScanning(true)}
                            startIcon={<QrCode />}
                            disabled={piecesRemaining === 0}
                        >
                            Start Scanning
                        </Button>
                    )}
                    {feedback.type && (
                        <Alert
                            severity={feedback.type}
                            sx={{ mt: 2 }}
                            onClose={clearFeedback}
                        >
                            {feedback.message}
                        </Alert>
                    )}
                </Paper>

                <Grid container spacing={2}>
                    {sortedItems.map((item) => {
                        const progress = getLineProgress(item);
                        const { isFullyPacked, isPartiallyPacked, piecesRemaining: lineRemaining, quantityOrdered, piecesPacked } = progress;
                        const lineTitle = getLineTitle(item);
                        const product = item.stockItem?.product;

                        return (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={item._id}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        p: 2,
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        opacity: isFullyPacked ? 0.5 : 1,
                                        pointerEvents: isFullyPacked ? 'none' : 'auto',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, flex: 1 }}>
                                        {item.isSoldOut && (
                                            <Chip label="Sold out" variant="outlined" size="small" sx={{ alignSelf: 'flex-start' }} />
                                        )}
                                        {!item.isSoldOut && (
                                            <PiecesRemainingBox
                                                count={lineRemaining}
                                                isFullyPacked={isFullyPacked}
                                                isPartiallyPacked={isPartiallyPacked}
                                                quantityOrdered={quantityOrdered}
                                                piecesPacked={piecesPacked}
                                            />
                                        )}
                                        <Avatar
                                            src={product?.image}
                                            sx={{ width: 80, height: 80 }}
                                        />
                                        <Typography
                                            variant="subtitle1"
                                            textAlign="center"
                                            fontWeight={isFullyPacked ? 'normal' : 'bold'}
                                            sx={{ textDecoration: isFullyPacked ? 'line-through' : 'none' }}
                                        >
                                            {lineTitle}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" textAlign="center">
                                            {product?.sku || product?.externalId}
                                        </Typography>

                                        <Divider sx={{ width: '100%', my: 1 }} />

                                        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            {product?.size && (
                                                <Typography variant="body2">
                                                    <strong>Size:</strong> {product.size}
                                                </Typography>
                                            )}
                                            {product?.weight && (
                                                <Typography variant="body2">
                                                    <strong>Weight:</strong> {product.weight}
                                                </Typography>
                                            )}
                                            {product?.color && (
                                                <Typography variant="body2">
                                                    <strong>Color:</strong> {product.color}
                                                </Typography>
                                            )}
                                        </Box>

                                    </Box>
                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>
            </Box>

            <Dialog
                open={scanning}
                onClose={() => setScanning(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 2 }
                }}
            >
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Scan Product QR Code
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Point the camera at the product's QR code
                    </Typography>

                    <Box sx={{ my: 3 }}>
                        <BarcodeScanner
                            width={300}
                            height={300}
                            onUpdate={(err, result) => {
                                if (result) handleScan(result.text);
                            }}
                        />
                    </Box>

                    <Button
                        variant="outlined"
                        onClick={() => setScanning(false)}
                        fullWidth
                    >
                        Cancel
                    </Button>
                </Box>
            </Dialog>
        </Container>
    );
}

const mapStateToProps = (state) => ({
    user: state?.auth?.user,
    warehouses: state?.records?.warehouses,
    orders: state?.records?.orders,
    order: state?.records?.order,
    orderItems: state?.records?.orderItems
});

const mapDispatchToProps = { getOrders, packOrderItem, finalizeOrder, cancelPackingOrder };

export default connect(mapStateToProps, mapDispatchToProps)(PackOrder);
